import { forwardRef } from 'react'
import Link from 'next/link'
import { cn } from '@/lib/utils'

const buttonVariants = {
  primary:
    'bg-primary-600 text-white hover:bg-primary-700 shadow-sm hover:shadow-md',
  secondary:
    'bg-panel text-ink border border-line hover:bg-page shadow-sm',
  ghost: 'text-muted hover:text-ink hover:bg-raised',
  dark: 'bg-ink text-page hover:opacity-90 shadow-md hover:shadow-lg',
  ai: 'bg-ai-action text-ai-on-action hover:opacity-90 shadow-md hover:shadow-lg',
  marketplace:
    'bg-sky-500 text-white hover:bg-sky-600 shadow-sm hover:shadow-md',
  link: 'text-brand-green hover:text-brand-green underline-offset-4 hover:underline',
}

const buttonSizes = {
  sm: 'h-8 px-3 text-sm rounded-md',
  md: 'h-10 px-4 text-sm rounded-lg',
  lg: 'h-12 px-6 text-base rounded-lg',
  xl: 'h-14 px-8 text-lg rounded-xl',
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof buttonVariants
  size?: keyof typeof buttonSizes
  href?: string
  external?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      href,
      external,
      children,
      ...props
    },
    ref
  ) => {
    const classes = cn(
      'inline-flex items-center justify-center gap-2 font-medium transition-all duration-200 disabled:opacity-50 disabled:pointer-events-none cursor-pointer',
      buttonVariants[variant],
      buttonSizes[size],
      className
    )

    // Button-only props should not be forwarded to anchor/link elements.
    const { disabled, type, ...rest } = props

    if (href) {
      if (external) {
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={classes}
            {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          >
            {children}
          </a>
        )
      }

      return (
        <Link
          href={href}
          className={classes}
          {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {children}
        </Link>
      )
    }

    return (
      <button ref={ref} className={classes} {...props}>
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'
